import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { colors, typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import NDADisclosure from "../components/NDADisclosure";
import { StepGrid, DomainGrid, CycleFlow } from "../components/diagrams/primitives";

/**
 * Leading Engineers.
 *
 * Replaces the old leadership-philosophy page. Same rule as How I Build: nothing
 * asserted that the site cannot show. The through-line is that on the teams I have
 * worked on, leadership was mostly structural — reducing what it costs other people
 * to understand a decision — rather than a management posture.
 */

const PageWrapper = styled.div`
    max-width: 920px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Meta = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};
    margin: ${spacing[500]} 0 ${spacing[600]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 72ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

/* Inline reference back to the case study a claim came from. Reads as part of the
   sentence rather than as a button, so the prose is not broken up by chrome. */
const Ref = styled(Link)`
    color: ${({ theme }) => theme.primary};
    border-bottom: 1px dotted ${({ theme }) => theme.primary};
    white-space: nowrap;

    &:hover {
        background: ${({ theme }) => theme.primary};
        color: ${({ theme }) => theme.background};
        text-shadow: none;
        border-bottom-color: transparent;
    }
`;

const REQUIREMENT_CARRIES = [
    { name: "Component behaviour", color: colors.primary[500] },
    { name: "Application states", color: colors.primary[500] },
    { name: "Interaction logic", color: colors.cyan[300] },
    { name: "Expected data", color: colors.cyan[300] },
    { name: "Technical constraints", color: colors.amber[400] },
    { name: "Acceptance criteria", color: colors.emerald[500] },
];

const CONSUMERS = [
    {
        name: "Designers",
        color: colors.primary[500],
        desc: "Working in Figma, and needing the library to mean the same thing the code means.",
    },
    {
        name: "Engineers",
        color: colors.cyan[300],
        desc: "Working in the component library, and needing decisions expressed as props and states rather than as pictures.",
    },
    {
        name: "Agents",
        color: colors.purple[400],
        desc: "Working in the repository, and needing the rules written where the work happens instead of in a document nobody opens.",
    },
];

const LeadingEngineers = () => (
    <PageWrapper>
        <Header>
            <PageHeadline>Leading Engineers</PageHeadline>
            <Meta>
                <TerminalLabel>Position</TerminalLabel>
                <span>Leadership as coordination cost, not as a management posture</span>
            </Meta>
            <Lede>
                I have led a five-person cross-functional team and coordinated with engineers in
                other time zones who owned parts of the platform I did not. Almost none of what made
                that work was motivational. It was structural:{" "}
                <strong>
                    lowering what it costs someone else to understand a decision, and making
                    correctness something the system carries rather than something a person has to
                    remember
                </strong>
                .
            </Lede>
        </Header>

        <Section>
            <SectionHeadline>Coordination Is the Real Cost</SectionHeadline>
            <Para>
                At <Ref to="/work/Caterpillar">Caterpillar</Ref> I led a five-person team while
                working alongside international developers responsible for other parts of the
                platform. Handing over screens would have been useless. Across time zones, an
                ambiguity does not get resolved in a hallway &mdash; it gets resolved a day later,
                after someone has already built against their own guess.
            </Para>
            <DiagramSlot>
                <StepGrid
                    label="What a Requirement Had to Carry"
                    steps={REQUIREMENT_CARRIES}
                    columns={3}
                    caption="Everything a distributed team would otherwise have to infer. The clearer the product and implementation contracts were, the less rework happened between teams."
                />
            </DiagramSlot>
            <Pull>
                Distance does not make people slower. Ambiguity does. Distance only makes the
                ambiguity expensive.
            </Pull>
        </Section>

        <Section>
            <SectionHeadline>Autonomy Needs a Contract</SectionHeadline>
            <Para>
                Giving people room to decide is easy to say and hard to sustain, because it only
                holds if the boundary between their decision and everyone else&rsquo;s is explicit.
                Without that, autonomy quietly becomes divergence, and the fix arrives later as a
                review process that slows everyone down.
            </Para>
            <Para>
                At <Ref to="/work/att">AT&amp;T</Ref> the same design system had three different
                kinds of consumer, and the contract had to hold for all of them at once.
            </Para>
            <DiagramSlot>
                <DomainGrid label="One System, Three Consumers" domains={CONSUMERS} />
            </DiagramSlot>
            <Para>
                That is why the rules became hard rules with identifiers rather than conventions in a
                document. A rule with a name can be pointed at, argued with, and revised. A
                convention can only be enforced by whoever happens to review the change &mdash; which
                makes consistency a function of who was available that day.
            </Para>
        </Section>

        <Section>
            <SectionHeadline>Put the Knowledge Where the Work Happens</SectionHeadline>
            <Para>
                The highest-leverage thing I have done for a team is not answering questions. It is
                moving the answer to where the question gets asked.
            </Para>
            <Para>
                Design-system knowledge living in the engineering environment rather than in
                documentation is what made AI-assisted development repeatable across engineers,
                instead of something each person got good at privately. The same principle drove{" "}
                <Ref to="/work/spectrum">Spectrum&rsquo;s</Ref> shared contract between product,
                design, and engineering: once the contract was in the component API, agreement stopped
                being a meeting.
            </Para>
            <Pull>
                Documentation that has to be remembered is a tax. Documentation that executes is
                infrastructure.
            </Pull>
        </Section>

        <Section>
            <SectionHeadline>Ownership Does Not End at Handoff</SectionHeadline>
            <Para>
                At <Ref to="/work/Verizon">Verizon</Ref> I stayed through implementation and QA,
                because implementation is where the conditions nobody specified show up. A team that
                hands work across a boundary and stops looking will keep rediscovering the same class
                of problem, one ticket at a time.
            </Para>
            <DiagramSlot>
                <CycleFlow
                    label="Where the Feedback Has to Land"
                    nodes={[
                        { name: "Decide", color: colors.primary[500] },
                        { name: "Build", color: colors.cyan[300] },
                        { name: "Ship", color: colors.emerald[500] },
                        { name: "Watch It Behave", color: colors.amber[400] },
                        { name: "Fix the Source", color: colors.coral[400] },
                    ]}
                    note="Back into the pattern, not into the ticket"
                    noteColor={colors.coral[400]}
                    caption="Fixing the instance closes the bug. Fixing the pattern closes every future instance of it, which is the only version that compounds across a team."
                />
            </DiagramSlot>
        </Section>

        <Section>
            <SectionHeadline>Teach the Reasoning, Not the Answer</SectionHeadline>
            <Para>
                Reviewing work by handing back a corrected version teaches nothing except that
                corrections arrive. What transfers is the reasoning: why this boundary, what breaks
                if the content changes, what this component is allowed to know.
            </Para>
            <Para>
                It is also the honest test of whether a decision was any good. A decision I cannot
                explain well enough for someone else to apply it to the next case was probably taste
                wearing the costume of a principle.
            </Para>
        </Section>

        <Section>
            <SectionHeadline>Where I Push Back</SectionHeadline>
            <Para>
                Requirements move &mdash; promotions change, priorities change, legal constraints
                appear, timelines compress. That is normal, and reusable systems exist precisely so
                that movement is absorbable. What I do argue about is scope that quietly relocates
                complexity onto the customer, and deadlines met by forking a pattern, because both
                are loans taken out against a team that has not agreed to repay them.
            </Para>
            <Pull>
                Every shortcut is a decision about someone else&rsquo;s next six months. It should be
                made out loud.
            </Pull>
        </Section>

        <NDADisclosure />
    </PageWrapper>
);

export default LeadingEngineers;
